import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const PriyaPage: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);

  const funnyQuotes = [
    "I can save lives but can't save my phone battery",
    "My stethoscope hears hearts better than my ears hear drama",
    "I'm a nurse by day, a superhero by night",
    "I've seen more blood than a vampire convention",
    "My patients think I'm an angel, my family knows I'm human",
    "I can diagnose you in 30 seconds but can't find my keys"
  ];

  const skills = [
    { name: 'Patient Care', level: 98, icon: '🩺' },
    { name: 'Emergency Response', level: 95, icon: '🚨' },
    { name: 'Medicine Knowledge', level: 92, icon: '💊' },
    { name: 'Compassion', level: 100, icon: '💕' },
    { name: 'Multi-tasking', level: 97, icon: '⚡' },
    { name: 'Future Doctor', level: 85, icon: '👩‍⚕️' }
  ];

  const hobbies = [
    { name: 'Managing Noor', icon: '👨‍💻', description: 'Keeping my programmer boyfriend organized and fed' },
    { name: 'Household CEO Duties', icon: '🏠', description: 'Running our home like a well-oiled machine' },
    { name: 'Cooking for Two', icon: '🍳', description: 'Making sure we both eat healthy (and delicious!)' },
    { name: 'Couple Adventures', icon: '✈️', description: 'Exploring the world together, one destination at a time' },
    { name: 'Photo Memories', icon: '📸', description: 'Capturing our love story, one picture at a time' },
    { name: 'Date Night Planning', icon: '💕', description: 'Creating perfect romantic moments for us' }
  ];

  const achievements = [
    { title: 'Life Saver', description: 'Successfully saved countless lives with care and expertise', icon: '🦸‍♀️' },
    { title: 'Future Doctor', description: 'Soon to be the first doctor in the family - making history!', icon: '👩‍⚕️' },
    { title: 'Compassion Champion', description: 'Known for her incredible empathy and patient care', icon: '💝' },
    { title: 'Future Wife', description: 'Successfully captured Noor\'s heart with her amazing personality', icon: '💕' }
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-900 via-pink-800 to-purple-900">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 bg-pink-400 rounded-full opacity-30"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-pink-400 to-rose-600 rounded-full flex items-center justify-center text-6xl shadow-2xl"
            >
              👩‍⚕️
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-5xl md:text-7xl font-bold text-white mb-4"
            >
              Hello, I'm <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">Priya</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-2xl md:text-3xl text-pink-200 mb-6"
            >
              I am a Nurse and Soon to be First Doctor in the Family! 🩺✨
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-lg text-pink-300 max-w-3xl mx-auto"
            >
              Healing hearts and saving lives, one patient at a time. When I'm not in scrubs, 
              I'm dreaming of the day I'll be called "Doctor Priya" and making my family proud!
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Funny Quotes Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-800 to-pink-800">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            Priya's Wisdom 💡
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {funnyQuotes.map((quote, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, rotate: Math.random() * 4 - 2 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <p className="text-white text-lg font-medium italic">"{quote}"</p>
                <div className="flex justify-end mt-4">
                  <span className="text-pink-300 text-sm">- Priya</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Wifey Powers Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-rose-800 to-pink-900">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            My Wifey Superpowers 👑
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                onHoverStart={() => setHoveredSkill(skill.name)}
                onHoverEnd={() => setHoveredSkill(null)}
                className="bg-gradient-to-br from-rose-600 to-pink-700 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-4xl">{skill.icon}</span>
                  <span className="text-white font-bold text-xl">{skill.level}%</span>
                </div>
                <h3 className="text-white text-xl font-bold mb-3">{skill.name}</h3>
                <div className="w-full bg-white/20 rounded-full h-3 mb-2">
                  <motion.div
                    className="bg-gradient-to-r from-rose-400 to-pink-500 h-3 rounded-full"
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
                    transition={{ duration: 1, delay: index * 0.1 + 0.5 }}
                  />
                </div>
                {hoveredSkill === skill.name && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-rose-200 text-sm mt-2"
                  >
                    {skill.name === 'Wife Management' ? 'I run this show! 👑' : 
                     skill.name === 'Household Control' ? 'Everything in perfect order! 🏠' :
                     skill.name === 'Noor Supervision' ? 'Keeping him in line! 👨‍💻' :
                     skill.name === 'Patient Care' ? 'Still saving lives! 🩺' :
                     skill.name === 'Decision Making' ? 'I make the calls! 🎯' :
                     'Soon to be Dr. Priya! 👩‍⚕️'}
                  </motion.p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hobbies Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-pink-700 to-rose-800">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            When I'm Not Being the Boss 👑
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hobbies.map((hobby, index) => (
              <motion.div
                key={hobby.name}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, rotateY: 5 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group"
              >
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {hobby.icon}
                </div>
                <h3 className="text-white text-xl font-bold mb-3">{hobby.name}</h3>
                <p className="text-purple-200">{hobby.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-rose-600 to-pink-700">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            Amazing Achievements 🏆
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <span className="text-4xl mr-4">{achievement.icon}</span>
                  <h3 className="text-white text-2xl font-bold">{achievement.title}</h3>
                </div>
                <p className="text-rose-100 text-lg">{achievement.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wifey Power Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-rose-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 border border-white/20"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-6xl mb-6"
            >
              👑
            </motion.div>
            <h2 className="text-4xl font-bold text-white mb-6">
              My Wifey Superpowers
            </h2>
            <p className="text-xl text-rose-100 mb-8">
              I run this relationship like I run the ICU - with precision, care, and lots of love! 
              Noor says I'm the boss, and he's absolutely right. I take charge of everything 
              from our schedule to his snacks, because that's what amazing wives do! 💪💕
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: '🏠', skill: 'Household CEO' },
                { icon: '👨‍💻', skill: 'Noor Manager' },
                { icon: '📅', skill: 'Schedule Master' },
                { icon: '💝', skill: 'Love Expert' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                  className="bg-white/20 rounded-xl p-4"
                >
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <div className="text-white text-sm font-medium">{item.skill}</div>
                </motion.div>
              ))}
            </div>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-4xl"
            >
              👩‍⚕️ + 👨‍💻 = 💕👑
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PriyaPage;
